import type {
  AccountingEntry,
  AccountingLine,
  MollieInvoice,
  MollieInvoiceMethodLine,
  MollieSettlement,
  MollieSettlementLine,
  MollieSettlementPeriod,
  MollieTransaction,
  PaymentAttribution,
  SalesInvoiceBasis,
  SalesInvoiceRow,
} from '../../../types/models/Reconciliation';
import { buildExcelCsv } from '../../../utils/excelCsv';
import type { ExcelCsvColumn } from '../../../utils/excelCsv';
import { formatBareDateNumeric, formatInstantBrussels } from '../../../utils/dateFormat';
import { slugifyForFilename } from '../../../utils/fileDownload';
import {
  DIRECTION_LABELS,
  DOMAIN_ORDER_STATUS_LABELS,
  DOMAIN_ORDER_TYPE_LABELS,
  ENTRY_STATUS_LABELS,
  JOURNAL_LABELS,
  MATCHED_BY_LABELS,
  MOLLIE_INVOICE_STATUS_LABELS,
  ORIGIN_LABELS,
  PAYE_LABELS,
  PAYMENT_METHOD_LABELS,
  PURPOSE_LABELS,
  SALES_TYPE_LABELS,
  SETTLED_VIA_LABELS,
  SETTLEMENT_LINE_KIND_LABELS,
  SETTLEMENT_STATUS_LABELS,
  SOURCE_LABELS,
  TRANSACTION_TYPE_LABELS,
  entryFlagsText,
  flagsText,
  labelOf,
} from './reconciliationLabels';

// The reconciliation CSV exports (Excel fr-BE dialect, see utils/excelCsv). Headers are French and mirror
// the on-screen columns; codes are exported as their French labels. An export always covers the WHOLE
// period the backend returned, whatever the on-screen filters. Flat lists export one row each; payouts and
// Mollie invoices nest, so they export one row per nested item (payout period / invoice line grouped per
// method), the parent's columns repeated. The Mollie invoice PDF link is a signed bearer URL: never exported.

const date = (v: string | null): string | null => (v === null ? null : formatBareDateNumeric(v));
const instant = (v: string | null): string | null => (v === null ? null : formatInstantBrussels(v));
const label = (map: Readonly<Record<string, string>>, v: string | null): string | null =>
  v === null ? null : labelOf(map, v);

/** The attribution columns shared by journal rows and settlement-detail lines. */
function attributionColumns<T extends PaymentAttribution>(): ExcelCsvColumn<T>[] {
  return [
    { header: 'Objet', kind: 'text', value: (r) => label(PURPOSE_LABELS, r.purpose) },
    { header: 'Source', kind: 'text', value: (r) => label(SOURCE_LABELS, r.source) },
    { header: 'Client', kind: 'text', value: (r) => r.client_name },
    { header: 'ID client Manager', kind: 'number', value: (r) => r.client_id },
    { header: 'Clé comptable', kind: 'number', value: (r) => r.accounting_key },
    { header: 'N° de facture', kind: 'text', value: (r) => r.invoice_numero },
    { header: 'ID facture Facturier', kind: 'number', value: (r) => r.facturier_invoice_id },
    { header: 'Tentative de paiement', kind: 'number', value: (r) => r.invoice_payment_id },
    { header: 'Commande de domaine', kind: 'number', value: (r) => r.domain_order_id },
    { header: 'Type de commande', kind: 'text', value: (r) => label(DOMAIN_ORDER_TYPE_LABELS, r.domain_order_type) },
    {
      header: 'Statut de commande',
      kind: 'text',
      value: (r) => label(DOMAIN_ORDER_STATUS_LABELS, r.domain_order_status),
    },
    { header: 'Domaines', kind: 'text', value: (r) => r.domains.join(', ') },
    { header: 'Rechargement', kind: 'number', value: (r) => r.wallet_deposit_id },
    { header: 'Enregistrement de carte', kind: 'number', value: (r) => r.payment_mandate_id },
    { header: 'Rapproché par', kind: 'text', value: (r) => label(MATCHED_BY_LABELS, r.matched_by) },
    { header: 'Signalements', kind: 'text', value: (r) => flagsText(r.flags) },
  ];
}

export const JOURNAL_CSV_COLUMNS: ExcelCsvColumn<MollieTransaction>[] = [
  { header: 'Date de comptabilisation', kind: 'date', value: (r) => date(r.booking_date) },
  { header: 'Comptabilisé le', kind: 'date', value: (r) => instant(r.booked_at) },
  { header: 'Créé chez Mollie le', kind: 'date', value: (r) => instant(r.created_at) },
  { header: 'Type', kind: 'text', value: (r) => labelOf(TRANSACTION_TYPE_LABELS, r.type) },
  { header: 'Sens', kind: 'text', value: (r) => labelOf(DIRECTION_LABELS, r.direction) },
  { header: 'Brut', kind: 'amount', value: (r) => r.gross },
  { header: 'Frais', kind: 'amount', value: (r) => r.fees },
  { header: 'Net', kind: 'amount', value: (r) => r.net },
  { header: 'Devise', kind: 'text', value: (r) => r.currency },
  { header: 'Description', kind: 'text', value: (r) => r.payment_description },
  { header: 'Moyen de paiement', kind: 'text', value: (r) => label(PAYMENT_METHOD_LABELS, r.payment_method) },
  { header: 'Payé le', kind: 'date', value: (r) => instant(r.payment_paid_at) },
  ...attributionColumns<MollieTransaction>(),
  { header: 'Versement', kind: 'text', value: (r) => r.settlement_reference },
  { header: 'Versé le', kind: 'date', value: (r) => instant(r.settlement_settled_at) },
  { header: 'Versement inféré', kind: 'text', value: (r) => (r.settlement_id === null ? null : r.settlement_inferred) },
  { header: 'Facture Mollie', kind: 'text', value: (r) => r.mollie_invoice_reference },
  { header: 'ID mouvement Mollie', kind: 'text', value: (r) => r.id },
  { header: 'ID paiement Mollie', kind: 'text', value: (r) => r.payment_id },
  { header: 'ID remboursement', kind: 'text', value: (r) => r.refund_id },
  { header: 'Description du remboursement', kind: 'text', value: (r) => r.refund_description },
  { header: 'ID rétrofacturation', kind: 'text', value: (r) => r.chargeback_id },
  { header: 'Description de la rétrofacturation', kind: 'text', value: (r) => r.chargeback_description },
  { header: 'ID versement', kind: 'text', value: (r) => r.settlement_id },
  { header: 'ID facture Mollie', kind: 'text', value: (r) => r.mollie_invoice_id },
];

export function buildJournalCsv(rows: MollieTransaction[]): string {
  return buildExcelCsv(JOURNAL_CSV_COLUMNS, rows);
}

/** A payout and one of its periods (`period` null for a payout without periods). */
interface SettlementPeriodRow {
  settlement: MollieSettlement;
  period: MollieSettlementPeriod | null;
}

export const SETTLEMENTS_CSV_COLUMNS: ExcelCsvColumn<SettlementPeriodRow>[] = [
  { header: 'Référence bancaire', kind: 'text', value: (r) => r.settlement.reference },
  { header: 'Statut', kind: 'text', value: (r) => label(SETTLEMENT_STATUS_LABELS, r.settlement.status) },
  { header: 'Créé le', kind: 'date', value: (r) => instant(r.settlement.created_at) },
  { header: 'Versé le', kind: 'date', value: (r) => instant(r.settlement.settled_at) },
  { header: 'Montant versé', kind: 'amount', value: (r) => r.settlement.amount },
  { header: 'Revenus du versement', kind: 'amount', value: (r) => r.settlement.revenue },
  { header: 'Frais du versement', kind: 'amount', value: (r) => r.settlement.costs },
  { header: 'Transactions du versement', kind: 'number', value: (r) => r.settlement.transactions_count },
  { header: 'Devise', kind: 'text', value: (r) => r.settlement.currency },
  { header: 'Période', kind: 'text', value: (r) => r.period?.period },
  { header: 'Facture Mollie de la période', kind: 'text', value: (r) => r.period?.mollie_invoice_reference },
  { header: 'Revenus de la période', kind: 'amount', value: (r) => r.period?.revenue },
  { header: 'Transactions de la période', kind: 'number', value: (r) => r.period?.revenue_count },
  { header: 'Frais de la période', kind: 'amount', value: (r) => r.period?.costs },
  { header: 'Frais de transaction', kind: 'amount', value: (r) => r.period?.costs_by_class.fee },
  { header: "Écart d'arrondi", kind: 'amount', value: (r) => r.period?.costs_by_class.rounding },
  { header: 'Compensation de facture', kind: 'amount', value: (r) => r.period?.costs_by_class.compensation },
  { header: 'Remise sur frais', kind: 'amount', value: (r) => r.period?.costs_by_class.discount },
  { header: 'Autre', kind: 'amount', value: (r) => r.period?.costs_by_class.other },
  { header: 'ID versement', kind: 'text', value: (r) => r.settlement.id },
  { header: 'ID facture Mollie', kind: 'text', value: (r) => r.period?.mollie_invoice_id },
];

export function buildSettlementsCsv(settlements: MollieSettlement[]): string {
  const rows = settlements.flatMap((settlement): SettlementPeriodRow[] =>
    settlement.periods.length === 0
      ? [{ settlement, period: null }]
      : settlement.periods.map((period) => ({ settlement, period })),
  );
  return buildExcelCsv(SETTLEMENTS_CSV_COLUMNS, rows);
}

/** A settlement-detail line, with the payout's bank reference repeated for context. */
interface SettlementLineRow {
  reference: string | null;
  line: MollieSettlementLine;
}

export const SETTLEMENT_LINES_CSV_COLUMNS: ExcelCsvColumn<SettlementLineRow>[] = [
  { header: 'Versement', kind: 'text', value: (r) => r.reference },
  { header: 'Type', kind: 'text', value: (r) => labelOf(SETTLEMENT_LINE_KIND_LABELS, r.line.kind) },
  { header: 'Créé le', kind: 'date', value: (r) => instant(r.line.created_at) },
  { header: 'Payé le', kind: 'date', value: (r) => instant(r.line.paid_at) },
  { header: 'Description', kind: 'text', value: (r) => r.line.description },
  { header: 'Moyen de paiement', kind: 'text', value: (r) => label(PAYMENT_METHOD_LABELS, r.line.method) },
  { header: 'Statut Mollie', kind: 'text', value: (r) => r.line.status },
  { header: 'Montant', kind: 'amount', value: (r) => r.line.amount },
  { header: 'Devise', kind: 'text', value: (r) => r.line.currency },
  ...attributionColumns<MollieSettlementLine>().map(
    (c): ExcelCsvColumn<SettlementLineRow> => ({ ...c, value: (r) => c.value(r.line) }),
  ),
  { header: 'ID', kind: 'text', value: (r) => r.line.id },
  { header: 'ID paiement Mollie', kind: 'text', value: (r) => r.line.payment_id },
];

export function buildSettlementLinesCsv(reference: string | null, lines: MollieSettlementLine[]): string {
  return buildExcelCsv(
    SETTLEMENT_LINES_CSV_COLUMNS,
    lines.map((line) => ({ reference, line })),
  );
}

/** A Mollie invoice and one of its per-method lines (`line` null for an invoice without lines). */
interface MollieInvoiceLineRow {
  invoice: MollieInvoice;
  line: MollieInvoiceMethodLine | null;
}

export const MOLLIE_INVOICES_CSV_COLUMNS: ExcelCsvColumn<MollieInvoiceLineRow>[] = [
  { header: 'Référence', kind: 'text', value: (r) => r.invoice.reference },
  { header: 'Statut', kind: 'text', value: (r) => label(MOLLIE_INVOICE_STATUS_LABELS, r.invoice.status) },
  { header: 'Émise le', kind: 'date', value: (r) => date(r.invoice.issued_at) },
  { header: 'Échéance', kind: 'date', value: (r) => date(r.invoice.due_at) },
  { header: 'Payée le', kind: 'date', value: (r) => date(r.invoice.paid_at) },
  { header: 'Périodes', kind: 'text', value: (r) => r.invoice.periods.join(', ') },
  { header: 'HTVA', kind: 'amount', value: (r) => r.invoice.net_amount },
  { header: 'TVA', kind: 'amount', value: (r) => r.invoice.vat_amount },
  { header: 'TVAC', kind: 'amount', value: (r) => r.invoice.gross_amount },
  { header: 'Devise', kind: 'text', value: (r) => r.invoice.currency },
  { header: 'Ligne (par moyen de paiement)', kind: 'text', value: (r) => r.line?.description },
  { header: 'Montant de la ligne', kind: 'amount', value: (r) => r.line?.amount },
  { header: 'Retenu par les versements', kind: 'amount', value: (r) => r.invoice.reconciliation.withheld },
  { header: 'Compensations', kind: 'amount', value: (r) => r.invoice.reconciliation.compensations },
  { header: 'Retenu net', kind: 'amount', value: (r) => r.invoice.reconciliation.settled_total },
  { header: 'Facturé', kind: 'amount', value: (r) => r.invoice.reconciliation.invoiced },
  { header: 'Écart', kind: 'amount', value: (r) => r.invoice.reconciliation.difference },
  { header: 'Concordance', kind: 'text', value: (r) => r.invoice.reconciliation.ok },
  { header: 'ID facture Mollie', kind: 'text', value: (r) => r.invoice.id },
];

export function buildMollieInvoicesCsv(invoices: MollieInvoice[]): string {
  const rows = invoices.flatMap((invoice): MollieInvoiceLineRow[] =>
    invoice.lines_by_method.length === 0
      ? [{ invoice, line: null }]
      : invoice.lines_by_method.map((line) => ({ invoice, line })),
  );
  return buildExcelCsv(MOLLIE_INVOICES_CSV_COLUMNS, rows);
}

export const SALES_CSV_COLUMNS: ExcelCsvColumn<SalesInvoiceRow>[] = [
  { header: 'N°', kind: 'text', value: (r) => r.numero },
  { header: 'Type', kind: 'text', value: (r) => labelOf(SALES_TYPE_LABELS, r.type) },
  { header: 'Date', kind: 'date', value: (r) => date(r.date) },
  { header: 'Client (Facturier)', kind: 'text', value: (r) => r.client_name },
  { header: 'Organisations Manager', kind: 'text', value: (r) => r.manager_client_names },
  { header: 'Clé comptable', kind: 'number', value: (r) => r.accounting_key },
  { header: 'Intitulé', kind: 'text', value: (r) => r.intitule },
  { header: 'Statut Facturier', kind: 'text', value: (r) => label(PAYE_LABELS, r.paye) },
  { header: 'Payée le (Facturier)', kind: 'date', value: (r) => date(r.paid_on) },
  { header: 'HTVA', kind: 'amount', value: (r) => r.amount_htva },
  { header: 'TVA', kind: 'amount', value: (r) => r.amount_tva },
  { header: 'TVAC', kind: 'amount', value: (r) => r.amount_tvac },
  { header: 'TVAC signé', kind: 'amount', value: (r) => r.signed_tvac },
  { header: 'Solde', kind: 'amount', value: (r) => r.solde },
  { header: 'Origine', kind: 'text', value: (r) => labelOf(ORIGIN_LABELS, r.origin) },
  { header: 'Réglée via', kind: 'text', value: (r) => labelOf(SETTLED_VIA_LABELS, r.settled_via) },
  { header: 'Encaissements', kind: 'number', value: (r) => r.collections },
  { header: 'Montant encaissé', kind: 'amount', value: (r) => r.settlement_amount },
  { header: 'Moyen Mollie', kind: 'text', value: (r) => label(PAYMENT_METHOD_LABELS, r.mollie_method) },
  { header: 'Payé chez Mollie le', kind: 'date', value: (r) => instant(r.mollie_paid_at) },
  { header: 'Versement', kind: 'text', value: (r) => r.mollie_settlement_reference },
  { header: 'Versé le', kind: 'date', value: (r) => instant(r.mollie_settled_at) },
  { header: 'Débité du solde le', kind: 'date', value: (r) => date(r.wallet_charged_on) },
  { header: 'Signalements', kind: 'text', value: (r) => flagsText(r.flags) },
  { header: 'ID facture Facturier', kind: 'number', value: (r) => r.facturier_invoice_id },
  { header: 'Créditée par (ID)', kind: 'number', value: (r) => r.credited_by_invoice_id },
  { header: 'Crédite la facture (ID)', kind: 'number', value: (r) => r.crediting_invoice_id },
  { header: 'Tentative de paiement', kind: 'number', value: (r) => r.invoice_payment_id },
  { header: 'ID paiement Mollie', kind: 'text', value: (r) => r.mollie_payment_id },
  { header: 'ID versement', kind: 'text', value: (r) => r.mollie_settlement_id },
  { header: 'Mouvement de solde', kind: 'number', value: (r) => r.wallet_transaction_id },
  { header: 'Commande de domaine', kind: 'number', value: (r) => r.domain_order_id },
  { header: 'Facture GPU', kind: 'number', value: (r) => r.gpu_server_invoice_id },
];

export function buildSalesCsv(rows: SalesInvoiceRow[]): string {
  return buildExcelCsv(SALES_CSV_COLUMNS, rows);
}

/** An accounting entry and one of its lines (entries are never empty). */
interface AccountingLineRow {
  entry: AccountingEntry;
  line: AccountingLine;
}

/**
 * One row per LINE, the entry's columns repeated — what Sophie encodes in WinAuditor, with its WinAuditor
 * encoding (type G / C / F + code) and comment. This CSV is for review: Excel reads a customer code such as
 * `0000004` as the number 4. The import path into WinAuditor is the per-OD .xlsx (« Exporter pour
 * WinAuditor »), which keeps the zeros.
 */
export const ACCOUNTING_CSV_COLUMNS: ExcelCsvColumn<AccountingLineRow>[] = [
  { header: 'Date', kind: 'date', value: (r) => date(r.entry.date) },
  { header: 'Journal', kind: 'text', value: (r) => labelOf(JOURNAL_LABELS, r.entry.journal) },
  { header: 'Pièce', kind: 'text', value: (r) => r.entry.reference },
  { header: 'Écriture', kind: 'text', value: (r) => r.entry.description },
  { header: 'Statut', kind: 'text', value: (r) => labelOf(ENTRY_STATUS_LABELS, r.entry.status) },
  { header: 'Compte', kind: 'text', value: (r) => r.line.account },
  { header: 'Intitulé du compte', kind: 'text', value: (r) => r.line.account_label },
  { header: 'Type WinAuditor', kind: 'text', value: (r) => r.line.winauditor_account?.type },
  { header: 'Code WinAuditor', kind: 'text', value: (r) => r.line.winauditor_account?.code },
  { header: 'Libellé', kind: 'text', value: (r) => r.line.label },
  { header: 'Débit', kind: 'amount', value: (r) => r.line.debit },
  { header: 'Crédit', kind: 'amount', value: (r) => r.line.credit },
  { header: 'Commentaire', kind: 'text', value: (r) => r.line.comment },
  { header: 'Répartition', kind: 'text', value: (r) => r.line.settles.text },
  { header: 'Signalements', kind: 'text', value: (r) => entryFlagsText(r.entry.flags) },
];

export function buildAccountingEntriesCsv(entries: AccountingEntry[]): string {
  return buildExcelCsv(
    ACCOUNTING_CSV_COLUMNS,
    entries.flatMap((entry) => entry.lines.map((line) => ({ entry, line }))),
  );
}

export const reconciliationCsvFilename = {
  journal: (month: string) => `rapprochement_journal-mollie_${month}.csv`,
  sales: (month: string, basis: SalesInvoiceBasis) =>
    `rapprochement_journal-ventes_${month}_${basis === 'paid' ? 'par-paiement' : 'par-emission'}.csv`,
  settlements: (year: number) => `rapprochement_versements_${year}.csv`,
  settlement: (reference: string | null, id: string) =>
    `rapprochement_versement_${slugifyForFilename(reference ?? id)}.csv`,
  mollieInvoices: (year: number) => `rapprochement_factures-mollie_${year}.csv`,
  accountingEntries: (month: string) => `comptabilite_ecritures_${month}.csv`,
};
